import { useTranslation } from 'react-i18next'
import { Facebook, Linkedin, Send } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'
import type { SupportedLanguage } from '@/i18n'

const navSlugMap: Record<string, Record<SupportedLanguage, string>> = {
  'chi-siamo': { it: 'chi-siamo', en: 'about-us' },
  'prodotti': { it: 'prodotti', en: 'products' },
  'contatti': { it: 'contatti', en: 'contacts' },
  'academy': { it: 'academy', en: 'academy' },
  'garanzia': { it: 'garanzia', en: 'warranty' },
  'privacy-policy': { it: 'privacy-policy', en: 'privacy-policy' },
  'cookie-policy': { it: 'cookie-policy', en: 'cookie-policy' },
  'dichiarazione-di-accessibilita': { it: 'dichiarazione-di-accessibilita', en: 'accessibility-statement' },
  'vulnerability-disclosure-policy': { it: 'vulnerability-disclosure-policy', en: 'vulnerability-disclosure-policy' },
}

function getLocalizedSlug(base: string, lang: SupportedLanguage): string {
  const mapping = navSlugMap[base]
  return mapping ? mapping[lang] : base
}

export function Footer() {
  const { t, i18n } = useTranslation()
  const lang = (i18n.language || 'it') as SupportedLanguage
  const year = new Date().getFullYear()

  return (
    <footer className="bg-primary text-white">
      <div className="container-main py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div>
            <img
              src="/media/logos/selea-logo-white.svg"
              alt="Selea"
              className="h-10 w-auto mb-4"
            />
            <p className="text-white/70 text-sm leading-relaxed mb-4">
              {t('footer.tagline')}
            </p>
            <div className="text-white/70 text-sm space-y-1">
              <p>Selea s.r.l.</p>
              <p>Via Aldo Moro 69</p>
              <p>Cicognara (MN), Italy</p>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h3 className="text-white font-semibold mb-4">{t('footer.navigation')}</h3>
            <ul className="space-y-2">
              {[
                { key: 'nav.about', slug: 'chi-siamo' },
                { key: 'nav.products', slug: 'prodotti' },
                { key: 'nav.academy', slug: 'academy' },

              ].map((item) => (
                <li key={item.key}>
                  <LocalizedLink
                    to={`/${getLocalizedSlug(item.slug, lang)}`}
                    className="text-white/70 hover:text-white transition-colors text-sm"
                  >
                    {t(item.key)}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-white font-semibold mb-4">{t('footer.contacts')}</h3>
            <ul className="space-y-2">
              <li>
                <LocalizedLink
                  to={`/${getLocalizedSlug('contatti', lang)}`}
                  className="text-white/70 hover:text-white transition-colors text-sm"
                >
                  {t('nav.contacts')}
                </LocalizedLink>
              </li>
            </ul>
            {/* Social links */}
            <div className="flex items-center gap-3 mt-6">
              <a
                href="https://www.facebook.com/SeleaSrl"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.linkedin.com/company/selea-srl"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href="https://t.me/seleasrl"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                aria-label="Telegram"
              >
                <Send className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-white font-semibold mb-4">{t('footer.legalNotes')}</h3>
            <ul className="space-y-2">
              {[
                { key: 'footer.privacy', slug: 'privacy-policy' },
                { key: 'footer.cookiePolicy', slug: 'cookie-policy' },
                { key: 'footer.accessibility', slug: 'dichiarazione-di-accessibilita' },
                { key: 'footer.vdp', slug: 'vulnerability-disclosure-policy' },
              ].map((item) => (
                <li key={item.key}>
                  <LocalizedLink
                    to={`/${getLocalizedSlug(item.slug, lang)}`}
                    className="text-white/70 hover:text-white transition-colors text-sm"
                  >
                    {t(item.key)}
                  </LocalizedLink>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="container-main py-4 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-white/50 text-sm">
            &copy; {year} Selea s.r.l. — {t('footer.allRightsReserved')}
          </p>
          <p className="text-white/40 text-xs">
            Part of TTM Group
          </p>
        </div>
      </div>
    </footer>
  )
}
